import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { getQueue } from "../../services/queue";
import { visibleGroups, rowText, egp } from "../../utils/queuePresentation";
import { tokens } from "../../utils/figmaTokens";

// One group: a header with the count, then its newest rows. An empty group
// collapses to its header (spec: "empty groups collapse") — it is still listed,
// so a clear queue reads as four green headers, not a blank page.
const GroupCard = ({ group, data, onOpen }) => {
  const clear = data.count === 0;
  return (
    <Card sx={{ mb: 2, borderRadius: 2, boxShadow: tokens.shadow.raised }}>
      <CardContent sx={{ p: 2, "&:last-child": { pb: clear ? 2 : 1 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, flexGrow: 1 }}>
            {group.title}
          </Typography>
          {group.key === "payouts" && !clear && (
            <Typography variant="body2" color="text.secondary">
              {egp(data.total)} · {data.period}
            </Typography>
          )}
          <Chip
            size="small"
            label={clear ? "Clear" : String(data.count)}
            color={clear ? "success" : "warning"}
            variant={clear ? "outlined" : "filled"}
          />
          <Button size="small" onClick={() => onOpen(group.viewAll)}>
            View all
          </Button>
        </Box>
        {!clear && (
          <>
            <Divider sx={{ mt: 1.5 }} />
            <List dense disablePadding>
              {data.rows.map((row) => {
                const text = rowText(group.key, row);
                return (
                  <ListItemButton key={row.id} onClick={() => onOpen(row.to)} sx={{ borderRadius: 1 }}>
                    <ListItemText primary={text.primary} secondary={text.secondary} />
                  </ListItemButton>
                );
              })}
            </List>
            {data.count > data.rows.length && (
              <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
                {data.count - data.rows.length} more — View all
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const Queue = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const includePayouts = hasPermission(user, permissions.EDIT_FINANCIAL);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getQueue({ includePayouts }));
    } catch (e) {
      console.error("Error loading the queue:", e);
      // Surfaced, not swallowed: an empty queue and a failed read must not look the same.
      setError(e.message || "Could not load the queue.");
    } finally {
      setLoading(false);
    }
  }, [includePayouts]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = visibleGroups(user);

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "flex-end", gap: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
            Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            What needs a person right now. Newest first; click a row to open it.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        data &&
        groups.map((group) => (
          <GroupCard key={group.key} group={group} data={data[group.key]} onOpen={navigate} />
        ))
      )}
    </Box>
  );
};

export default Queue;
